<i18n>
{
  "en": {
    "label": {
      "yes": "Yes",
      "no": "No",
      "note": "Note",
      "submit": "Submit"
    },
    "message": {
      "title": "prior confirmation",
      "description": "Before confirming your identity, please perform the following preliminary confirmation. Once you have completed the preliminary confirmation, please click the 'Submit' button to proceed to the identity verification procedure.",
      "errorMessage": "Error has occurred. Please check the error details.",
      "isAgreeRequired": "Answer not selected.",
      "noteMaxLength": "Please enter up to {count} characters in the Notes field."
    }
  },
  "ja": {
    "label": {
      "yes": "はい",
      "no": "いいえ",
      "note": "備考",
      "submit": "送信する"
    },
    "message": {
      "title": "事前確認事項について",
      "description": "本人確認前に以下の事前確認を実施してください。事前確認が完了しましたら、「送信する」ボタンを押して、本人確認手続きへ進んでください。",
      "errorMessage": "エラーが発生しました。エラー内容をご確認ください。",
      "isAgreeRequired": "回答が選択されていません。",
      "noteMaxLength": "備考欄は{count}文字以内で入力してください。"
    }
  }
}
</i18n>

<template>
  <v-flex xs12 sm12 md12 lg12>
    <Infobox
      :title="$t('message.title')"
    >
      <p class="subheading">{{ $t('message.description') }}</p>
      <div v-for="(applicantCheckList, index) in this.applicantCheckLists" :key="applicantCheckList.checkListId">
        <div class="checkArea">
          <div class="contents">{{ index + 1 }}．{{ campaign.checkLists[index].contents }}</div>
          <div class="notes">{{ campaign.checkLists[index].notes }}</div>
          <v-radio-group v-model="applicantCheckList.isAgree" row style="margin-bottom: -16px;">
            <v-radio value="true" :label="$t('label.yes')" />
            <v-radio value="false" :label="$t('label.no')" />
          </v-radio-group>
          <v-text-field
            single-line
            v-model="applicantCheckList.notes"
            :label="$t('label.note')"
          ></v-text-field>
          <div v-if="errorMessages[applicantCheckList.checkListId]" class="errorMessage">
            {{errorMessages[applicantCheckList.checkListId]}}
          </div>
        </div>
      </div>
      <div class="errorArea" v-if="errorMessage">
        {{errorMessage}}
      </div>
      <div class="actionArea" v-if="applicantCheckLists && applicantCheckLists.length > 0">
        <v-btn color="primary" @click="clickButton" :disabled="sendButtonDisabled">
          {{$t('label.submit')}}
        </v-btn>
      </div>
    </Infobox>
  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex';
import Infobox from '@/components/atoms/Infobox.vue';
import { ConstCheckList } from '@/const/const.js';
import { Me } from '@/api';

export default {
  components: {
    Infobox
  },
  props: {
    user: {
      type: Object
    },
    done: {
      type: Function
    }
  },
  data() {
    return {
      applicantCheckLists: [],
      errorMessage: '',
      errorMessages: [],
      sendButtonDisabled: false
    };
  },
  mounted: function() {
    // チェックリスト生成
    if (this.campaign.checkLists && this.applicantCheckLists.length == 0) {
      this.createApplicantCheckLists();
    }
  },
  methods: {
    clickButton() {
      // 入力チェック
      this.validation()

      // チェックリスト回答内容登録
      Me.postApplicantCheckLists(this.applicantCheckLists)
        .then((result) => {
          // 完了したのでボタンは押下不可
          this.sendButtonDisabled = true;
          // 完了したので、親コンポーネントの再読み込み処理を呼び出す
          this.done();
        })
        .catch((error) => {
          for (const err of error.errors) {
            this.errorMessage = this.$t('message.errorMessage');
            if (!this.errorMessages[err.resource]) { // 他のエラーが設定されていない場合
              this.$set(this.errorMessages, err.resource, err.code)
            }
          }
        });
    },
    validation() {
      // エラーを初期化
      this.errorMessage = '';
      this.errorMessages = [];

      for (const applicantCheckList of this.applicantCheckLists) {
        // 回答の選択状態チェック
        if (applicantCheckList.isAgree != "true" && applicantCheckList.isAgree != "false") {
          this.errorMessage = this.$t('message.errorMessage');
          this.$set(this.errorMessages, applicantCheckList.checkListId, this.$t('message.isAgreeRequired'));
        }
        // 備考の文字数チェック
        if (applicantCheckList.notes && applicantCheckList.notes.length > ConstCheckList.MaxNotesLength) {
          this.errorMessage = this.$t('message.errorMessage');
          this.$set(this.errorMessages, applicantCheckList.checkListId, this.$t('message.noteMaxLength', { count: ConstCheckList.MaxNotesLength }));
        }
      }
    },
    async createApplicantCheckLists() {
      const applicantCheckListHash = await Me.fetchApplicantCheckLists()
        .then((result) => {
          const hash = {};
          for (const applicantCheckList of result.data.applicantCheckLists) {
            hash[applicantCheckList.checkListId] = applicantCheckList;
          }
          return hash;
        })
        .catch((error) => {
          console.error(error);
        });

      for (const checkList of this.campaign.checkLists) {
        const appCheckList = applicantCheckListHash[checkList.id];
        let isAgree = null;
        if (appCheckList && appCheckList.isAgree === true) {
          isAgree = "true"
        } else if (appCheckList && appCheckList.isAgree === false) {
          isAgree = "false"
        }
        this.applicantCheckLists.push({
          checkListId: checkList.id,
          isAgree: isAgree,
          notes: (appCheckList && appCheckList.notes) ? appCheckList.notes : ''
        })
      }
    }
  },
  computed: {
    ...mapGetters({ campaign: 'getCurrentCampaign' })
  },
  watch: {
    'campaign.checkLists': function(val) {
      this.createApplicantCheckLists();
    }
  }
};
</script>

<style scoped>
.checkArea {
  padding: 20px;
}

.contents {
  font-size: 1.2rem;
}

.notes {
  font-size: 1rem;
  padding: 4px;
  color: #555555;
}

.errorArea {
  padding: 16px;
  color: red;
  font-size: 1rem;
  display: flex;
}

.errorMessage {
  color: red;
}

.actionArea {
  padding: 32px;
  display: flex;
  flex-direction: row;
  justify-content: center;
}
</style>
